import type { FC, SVGProps } from "react"
import React from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import styled, { css } from "styled-components"

import { NestedRadio } from "~/components/ui"
import { currentUserIsDesignRequestUserState } from "~/containers/Proposal/state/user/user-state"
import IS_DEV from "~/utils/isDev"
import IS_QA from "~/utils/isQa"
import { fontWeight } from "~/utils/styles"

import { ReactComponent as Draw } from "@svg/tools/draw.svg"
import { ReactComponent as EdgeDesignator } from "@svg/tools/edge-detection.svg"
import { ReactComponent as Erase } from "@svg/tools/erase.svg"
import { ReactComponent as GroundMount } from "@svg/tools/ground-mount.svg"
import { ReactComponent as Move } from "@svg/tools/move.svg"
import { ReactComponent as Pan } from "@svg/tools/pan.svg"
import { ReactComponent as PanelCreatorLandscape } from "@svg/tools/panel-creation-landscape.svg"
import { ReactComponent as PanelCreatorPortrait } from "@svg/tools/panel-creation-portrait.svg"
import { ReactComponent as PanelEraser } from "@svg/tools/panel-eraser.svg"
import { ReactComponent as PanelActivator } from "@svg/tools/panel-toggle.svg"
import { ReactComponent as Ruler } from "@svg/tools/ruler.svg"
import { ReactComponent as Select } from "@svg/tools/select.svg"
import { ReactComponent as Arrow } from "@svg/tools/sub-menu-icon.svg"
import { ReactComponent as Trees } from "@svg/tools/trees.svg"

import {
  phaseState,
  userHasRequiredPermissionsForPhaseState,
} from "../state/phase"
import type { Tool } from "../state/tool"
import {
  TOOL_LABELS,
  TOOL_DESC,
  TOOL_HOTKEYS,
  findToolTrayLastUsedState,
  openToolTrayState,
  enabledToolsState,
  toolState,
} from "../state/tool"

const Wrapper = styled.div`
  display: inline-flex;
  flex-flow: column;
  position: relative;
  top: 10px;
  left: 10px;
  z-index: var(--z-toolbar);
`

export const radioCss = css`
  display: inline-flex;
  flex-shrink: 0;
  flex-grow: 1;
  flex-flow: column;
  padding: 0 10px;
  background: white;
  border-radius: 5px;
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.25);
  > * ~ * {
    border-top: 1px solid var(--grey-border);
  }
  div[data-role="option-with-tray-wrapper"] {
    position: relative;
    display: inline-flex;
    [data-role="tray-indicator"] {
      display: inline-block;
      position: absolute;
      bottom: 9px;
      right: 1px;
      svg {
        width: 12px;
        height: 8px;
        > * {
          fill: var(--white);
          stroke: var(--grey-dark);
        }
      }
    }
    [data-role="tray"] {
      padding: 0 10px;
      position: absolute;
      box-shadow: 0px 10px 10px rgba(0, 0, 0, 0.5);
      z-index: var(--z-toolbar);
      background-color: var(--white);
      border-radius: 4px;
      left: 140%;
      top: -7;
      width: 175px;
      [data-role="option-name"] {
        padding: 0 12px 0 6px;
        cursor: pointer;
        display: block;
        font-size: 13px;
        line-height: 16px;
      }
    }
  }
  label[data-role="option"] {
    padding: 10px 0;
    position: relative;
    display: inline-flex;
    align-items: center;
    font-size: 20px;
    cursor: pointer;
    background: white;
    :hover > [data-role="tooltip"] {
      opacity: 1;
    }
    span[data-role="option-name"] {
      display: none;
    }
    [data-role="panel"] {
      padding: 5px;
      width: 36px;
      height: 36px;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      svg * {
        fill: var(--grey-dark);
      }
    }
    [data-role="tooltip"] {
      position: absolute;
      display: flex;
      gap: 10px;
      align-items: center;
      justify-content: flex-start;
      background-color: var(--blue);
      min-height: 50px;
      width: fit-content;
      height: fit-content;
      padding: 15px 15px;
      border-radius: 6px;
      left: 160%;
      top: 0;
      box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.15);
      opacity: 0;
      transition: opacity 0.5s;
      pointer-events: none;
      width: fit-content;
      white-space: nowrap;
      svg * {
        fill: var(--grey-dark);
      }
      h3 {
        font-size: 16px;
        color: white;
        font-weight: 600;
      }
      p {
        color: white;
        font-size: 16px;
      }
      > div {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 5px;
      }
      ::after {
        content: "";
        position: absolute;
        top: 30px;
        right: 100%;
        margin-top: -10px;
        border-width: 10px;
        border-style: solid;
        border-color: transparent var(--blue) transparent transparent;
      }
    }
    input[type="radio" i] {
      border: none;
      position: relative;
      height: 0px;
      width: 0px;
      cursor: default;
      appearance: unset;
      box-sizing: border-box;
      outline: none;
      &:checked {
        border-color: var(--grey-dark);
        &::before {
          content: "";
          position: absolute;
          top: 4px;
          left: 4px;
          bottom: 4px;
          right: 4px;
          background: var(--blue);
        }
        ~ [data-role="option-name"] {
          color: var(--blue);
          ${fontWeight(600)}
        }
        ~ [data-role="panel"] {
          background: #e0eeff;
          border: 1px solid #96c6ff;
          svg * {
            fill: var(--blue);
          }
        }
      }
      &:disabled {
        ~ [data-role="panel"] svg * {
          fill: #ccc;
        }
      }
    }
  }
`

const TOOL_ICONS: Record<Tool, FC<SVGProps<SVGSVGElement>>> = {
  Select,
  Draw,
  Erase,
  GroundMount,
  Pan,
  PanelActivator,
  PanelCreatorPortrait,
  PanelCreatorLandscape,
  PanelEraser,
  Trees,
  EdgeDesignator,
  Move,
  Ruler,
}

export const Toolbar_Internal: FC = () => {
  const enabledTools = useRecoilValue(enabledToolsState)
  const hasRequiredPermissions = useRecoilValue(
    userHasRequiredPermissionsForPhaseState(useRecoilValue(phaseState)),
  )
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )

  const { t } = useTranslation()

  return (
    <Wrapper>
      <NestedRadio
        state={toolState}
        trayState={{
          findLastUsedIndexState: findToolTrayLastUsedState,
          openTrayState: openToolTrayState,
        }}
        options={
          hasRequiredPermissions && currentUserIsDesignUser ? enabledTools : []
        }
        css={radioCss}
        getName={(tool) =>
          `${t(TOOL_LABELS[tool])} [${TOOL_HOTKEYS[tool].toUpperCase()}]`
        }
        getSummary={(tool) => `${t(TOOL_DESC[tool])}`}
        shouldHide={(tool) => tool === `Ruler` && !IS_DEV && !IS_QA}
        icon={{
          get: (opt) => {
            const tool = TOOL_ICONS[opt]

            return tool
          },
          TrayIndicator: Arrow,
        }}
      />
    </Wrapper>
  )
}
export const Toolbar = React.memo(Toolbar_Internal)
